// `buscar_productos`: el agente busca en el catálogo por SKU, categoría o características, no sólo
// por el nombre que ve en su lista (ADR-2026-10-22-el-agente-busca-productos-por-sku-categoria-y-caracteristicas).
// Y LISTA: sin consulta devuelve el catálogo entero de a páginas, con `solo_con_sku` los que tienen
// SKU. "Dame todos los SKU" no es una búsqueda: sin esto el agente adivinó palabras durante 12 rondas y
// el turno terminó callado (ADR-2026-10-22-el-agente-busca-productos-por-sku-categoria-y-caracteristicas-2).
//
// Es la MISMA búsqueda que los buscadores de la app (`buscarProductos` sobre el índice de
// `indiceDeBusquedaDeProductos`): lo que encuentra la persona en Mi marca lo encuentra el agente. Puro:
// el índice llega armado.

import { buscarProductos } from '../../lib/busquedaDeProductos';
import type { ResumenParaBuscar } from '../../lib/indiceDeBusquedaDeProductos';

/** Lo más que vuelve por llamada (una página al listar). */
export const MAX_RESULTADOS = 200;
/** Lo que vuelve una búsqueda si no pide otra cosa: más ya no es "encontrar", es listar. */
const POR_DEFECTO_AL_BUSCAR = 50;

export interface ProductoBuscable {
  id: string;
  name: string;
}

export interface OpcionesDeBusqueda {
  limite?: number;
  /** Desde qué posición (0 = la primera), para seguir una lista larga. */
  desde?: number;
  /** Sólo los que tienen SKU. */
  soloConSku?: boolean;
}

/** La consulta del agente, o null = listar (sin consulta, o una de menos de 2 letras). */
export function consultaDe(input: unknown): string | null {
  const q = (input as { consulta?: unknown } | null)?.consulta;
  if (typeof q !== 'string') return null;
  const t = q.trim().replace(/\s+/g, ' ');
  return t.length >= 2 ? t.slice(0, 120) : null;
}

/** El tope que pidió, dentro de 1..MAX_RESULTADOS. Sin pedir: 50 al buscar, una página entera al listar. */
export function limiteDe(input: unknown, buscando: boolean): number {
  const n = (input as { limite?: unknown } | null)?.limite;
  if (typeof n !== 'number' || !Number.isFinite(n)) return buscando ? POR_DEFECTO_AL_BUSCAR : MAX_RESULTADOS;
  return Math.min(MAX_RESULTADOS, Math.max(1, Math.floor(n)));
}

export function desdeDe(input: unknown): number {
  const n = (input as { desde?: unknown } | null)?.desde;
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

export function soloConSkuDe(input: unknown): boolean {
  return (input as { solo_con_sku?: unknown } | null)?.solo_con_sku === true;
}

/**
 * El texto que vuelve: cuántos hay y, por cada uno, su id, su nombre, su SKU y sus categorías (así el
 * agente sabe por qué coincidió sin abrir nada). Con más de una página, desde dónde seguir.
 */
export function resultadoDeBusqueda(
  productos: readonly ProductoBuscable[],
  consulta: string | null,
  indice: ReadonlyMap<string, string>,
  resumen: (id: string) => ResumenParaBuscar | undefined,
  opciones: OpcionesDeBusqueda = {},
): string {
  const soloConSku = opciones.soloConSku === true;
  // Sin los datos no se sabe qué SKU tiene cada uno: decir "ninguno tiene" sería inventar. Se mira cuáles
  // de ESTOS productos se leyeron, no si el índice tiene algo: es compartido y puede traer los de otra
  // marca (review Greptile del PR #198).
  const sinDatos = productos.filter((p) => !indice.has(p.id)).length;
  if (soloConSku && productos.length > 0 && sinDatos === productos.length) {
    return 'No pude leer los SKU del catálogo en este momento. Decíselo y ofrecé probar de nuevo en un rato; no inventes SKU.';
  }
  const base = consulta ? buscarProductos([...productos], consulta, (p) => p.name, (p) => p.id, indice) : [...productos];
  const encontrados = soloConSku ? base.filter((p) => !!resumen(p.id)?.sku) : base;

  const que = consulta ? `coinciden con "${consulta}"${soloConSku ? ' y tienen SKU' : ''}` : soloConSku ? 'con SKU' : 'en el catálogo';
  if (encontrados.length === 0) {
    return consulta
      ? `Ningún producto ${que} (busqué en el nombre, el SKU, las categorías, las características y la descripción). ` +
          `Probá con menos palabras o con otra forma de decirlo; no inventes ids.`
      : `No hay productos ${que}.`;
  }

  const limite = Math.min(MAX_RESULTADOS, Math.max(1, opciones.limite ?? (consulta ? POR_DEFECTO_AL_BUSCAR : MAX_RESULTADOS)));
  const desde = Math.min(Math.max(0, opciones.desde ?? 0), encontrados.length);
  const mostrados = encontrados.slice(desde, desde + limite);
  const hasta = desde + mostrados.length;

  const lineas = mostrados.map((p) => {
    const r = resumen(p.id);
    const extra = [r?.sku ? `SKU ${r.sku}` : null, r?.categorias.length ? `categoría ${r.categorias.join(' / ')}` : null]
      .filter(Boolean)
      .join(' · ');
    return `- id=${p.id} · ${p.name}${extra ? ` · ${extra}` : ''}`;
  });

  const total = encontrados.length;
  const nombre = (n: number) => (n === 1 ? 'producto' : 'productos');
  const cabeza =
    desde === 0 && hasta === total
      ? consulta
        ? `${total} ${nombre(total)} ${total === 1 ? que.replace('coinciden', 'coincide').replace('tienen', 'tiene') : que}:`
        : `${total} ${nombre(total)} ${que}:`
      : `${total} productos ${que}; van del ${desde + 1} al ${hasta} de ${total}:`;
  const sigue =
    hasta < total
      ? `\nHay más: para seguir, llamá de nuevo con lo mismo y desde: ${hasta}. Si es para mostrarle la lista a la persona, ` +
        `juntá las páginas y armale una planilla en vez de pegar cientos de líneas en el chat.`
      : '';
  const incompleta =
    soloConSku && sinDatos > 0
      ? `\nOjo: no pude leer los datos de ${sinDatos} ${nombre(sinDatos)}, así que puede haber más con SKU. Decíselo a la persona.`
      : '';
  return `${cabeza}\n${lineas.join('\n')}${sigue}${incompleta}\nUsá estos id como product_id (referencias, lotes, pick_product).`;
}
